import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Types
import { AppDispatch, SessionExpiryDataProps } from 'types/RootState.types';

// Config
import {
  announceSessionExpiring,
  announceSessionExpired,
} from '@redux/actions/app/appActions';
import sessionExpiryCounterState from './SessionExpiryCounter.state';

const SessionExpiryCounter = () => {
  const dispatch: AppDispatch = useDispatch();

  const {
    sessionExpiringAnnounced,
    sessionExpiredAnnounced,
    sessionExpiryData = {} as SessionExpiryDataProps,
    sessionWarningAfterTime = 0,
  } = useSelector(sessionExpiryCounterState);

  const sessionExpiringAnnouncedRef = useRef(sessionExpiringAnnounced);
  const sessionExpiredAnnouncedRef = useRef(sessionExpiredAnnounced);
  const sessionExpiryDataRef = useRef(sessionExpiryData);
  const sessionWarningAfterTimeRef = useRef(sessionWarningAfterTime);

  useEffect(() => {
    sessionExpiringAnnouncedRef.current = sessionExpiringAnnounced;
    sessionExpiredAnnouncedRef.current = sessionExpiredAnnounced;
    sessionExpiryDataRef.current = sessionExpiryData;
    sessionWarningAfterTimeRef.current = sessionWarningAfterTime;
  }, [
    sessionExpiringAnnounced,
    sessionExpiredAnnounced,
    sessionExpiryData,
    sessionWarningAfterTime,
  ]);

  useEffect(() => {
    sessionCountdown();
    return () => {
      if (countdownIntervalRef.current !== null) {
        window.cancelAnimationFrame(countdownIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!sessionExpiringAnnounced) {
      if (countdownIntervalRef.current !== null) {
        window.cancelAnimationFrame(countdownIntervalRef.current);
      }
      sessionCountdown();
    }
  }, [sessionExpiringAnnounced, sessionExpiryData]);

  const startedAtRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);

  const count = () => {
    if (sessionExpiredAnnouncedRef.current) return;

    const elapsedTime = Date.now() - (startedAtRef.current ?? 0);
    const sessionExpiresIn = Number(sessionExpiryDataRef.current.sessionExpiresIn);

    const sessionIsExpiring =
      elapsedTime >=
      (sessionExpiryDataRef.current.sessionWarningAfterTime ||
        sessionWarningAfterTimeRef.current);
    const sessionHasExpired = elapsedTime >= sessionExpiresIn;

    if (sessionHasExpired) {
      if (countdownIntervalRef.current !== null) {
        window.cancelAnimationFrame(countdownIntervalRef.current);
      }
      dispatch(announceSessionExpired());
      return;
    }

    if (sessionIsExpiring && !sessionExpiringAnnouncedRef.current) {
      dispatch(announceSessionExpiring());
    }

    countdownIntervalRef.current = window.requestAnimationFrame(count);
  };

  const sessionCountdown = () => {
    const expiresIn = sessionExpiryDataRef.current?.sessionExpiresIn;
    const warningTime = sessionWarningAfterTimeRef.current;

    if (!expiresIn || !warningTime) return;

    startedAtRef.current = Date.now();
    countdownIntervalRef.current = window.requestAnimationFrame(count);
  };

  return null;
};

export default SessionExpiryCounter;
